import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import apiClient from '../utils/apiClient';

const Dashboard = () => {
  // 대시보드 데이터 상태
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // 대시보드 데이터 로드
  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.get('/Customer/GetDashBoard');
      if (response.success) {
        setDashboardData(response.data);
      } else {
        console.error('대시보드 데이터 로드 실패:', response.message);
      }
    } catch (error) {
      console.error('대시보드 데이터 로드 실패:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    loadDashboardData();
  }, []);

  // 로딩 중이거나 데이터가 없을 때 기본값 설정
  if (isLoading) {
    return (
      <div className="dashboard-container">
        <main className="dashboard">
          <div className="dashboard-header">
            <h1>대시보드</h1>
            <p>데이터를 불러오는 중...</p>
          </div>
          <div className="loading-spinner">
            <div className="spinner"></div>
          </div>
        </main>
      </div>
    );
  }

  // API 응답 데이터를 기반으로 통계 카드 생성
  const statsData = [
    {
      title: '금일 열람 고객수',
      value: dashboardData?.customerView?.today || '0',
      change: (dashboardData?.customerView?.gap != 0 && dashboardData?.customerView?.gap > 0 ? '+' : '') + dashboardData?.customerView?.gap + '건' || '+0건',
      changeType: 'positive',
      subtitle: '전일 대비',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-eye w-4 h-4 text-blue-600" aria-hidden="true"><path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path><circle cx="12" cy="12" r="3"></circle></svg>
      ),
      color: 'blue'
    },
    {
      title: '총 고객 생성수',
      value: dashboardData?.createCustomer?.total || '0',
      change: (dashboardData?.createCustomer?.today - dashboardData?.createCustomer?.yesterDay > 0 ? '+' : '') + (dashboardData?.createCustomer?.today - dashboardData?.createCustomer?.yesterDay) + '건' || '+0건',
      changeType: 'positive',
      subtitle: '전일 대비',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-users w-4 h-4 text-green-600" aria-hidden="true"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><path d="M16 3.128a4 4 0 0 1 0 7.744"></path><path d="M22 21v-2a4 4 0 0 0-3-3.87"></path><circle cx="9" cy="7" r="4"></circle></svg>
      ),
      color: 'green',
      detail:[
        { label: '플레이스', value: dashboardData?.createCustomer?.detail?.total?.place || '0' },
        { label: '인스타', value: dashboardData?.createCustomer?.detail?.total?.insta || '0' },
        { label: '블로그', value: dashboardData?.createCustomer?.detail?.total?.blog || '0' },
        { label: '기타', value: dashboardData?.createCustomer?.detail?.total?.etc || '0' },
      ]
    },
    {
      title: '총 업로드',
      value: '',
      subtitle: '일, 주, 월',
      stats: [
        { label: '일', value: `${dashboardData?.uploadContent?.today || 0}건` },
        { label: '주', value: `${dashboardData?.uploadContent?.weekend || 0}건` },
        { label: '월', value: `${dashboardData?.uploadContent?.month || 0}건` }
      ],
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-upload w-4 h-4 text-orange-600" aria-hidden="true"><path d="M12 3v12"></path><path d="m17 8-5-5-5 5"></path><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path></svg>
      ),
      color: 'orange'
    },
    {
      title: '알림톡 발송',
      value: (dashboardData?.alimtalk?.success + dashboardData?.alimtalk?.fail) || '0',
      subtitle: `성공 ${(dashboardData?.alimtalk?.success / (dashboardData?.alimtalk?.total) * 100).toFixed(2) || '0.0'}%`,
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-message-square w-4 h-4 text-yellow-600" aria-hidden="true"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
      ),
      color: 'yellow'
    }
  ];

  const industryData = [
    { name: 'IT/소프트웨어', percentage: 35, color: '#3b82f6' },
    { name: '제조업', percentage: 25, color: '#10b981' },
    { name: '서비스업', percentage: 20, color: '#8b5cf6' },
    { name: '유통업', percentage: 15, color: '#f59e0b' },
    { name: '기타', percentage: 5, color: '#6b7280' }
  ];

  return (
    <div className="dashboard-container">
      <main className="dashboard">
      <div className="dashboard-header">
        <h1>대시보드</h1>
        <p>전체 현황 및 통계</p>
      </div>

      {/* 이번 달 성과 섹션 */}
      <div className="team-performance">
        <div className="performance-card">
            <div className="performance-title">
                <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="24" height="24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path></svg>
                <span>이번 달 성과</span>
            </div>
            <div className="performance-sub-title">
                <span>전월 대비 고객 생성수</span>
            </div>
            <div className="performace-count-box">
                <div className="performance-count">
                    <div className={`performance-count-title ${dashboardData?.createCustomer?.gap >= 0 ? 'positive' : 'negative'}`}>{(dashboardData?.createCustomer?.gap != 0 && dashboardData?.createCustomer?.gap > 0 ? '+' : '') + dashboardData?.createCustomer?.gap || '0'}건</div>
                    <div className="performance-count-sub-title">고객 생성</div>
                </div>
                {/* <div className="performance-percent">
                    <div className="performance-percent-title">{dashboardData?.createCustomer?.growthRate || '0.0'}%</div>
                    <div className="performance-percent-sub-title">성장률</div>
                </div> */}
            </div>
        </div>
      </div>

      {/* 통계 카드들 */}
      <div className="stats-grid">
        {statsData.map((stat, index) => (
          <div key={index} className={`stat-card ${stat.color}`}>
            <div className="stat-header">
              <div className="stat-icon">{stat.icon}</div>
              <span className="stat-title">{stat.title}</span>
            </div>
            <div className="stat-content">
              {stat.value && (
                <span className="stat-value">{stat.value}</span>
              )}
              {stat.change && (
                <span className={`stat-change ${stat.changeType}`}>
                  {stat.change} {stat.subtitle}
                </span>
              )}
              {stat.stats && (
                <div className="stat-breakdown">
                  {stat.stats.map((item, idx) => (
                    <div key={idx} className="breakdown-item">
                      <span className="breakdown-label">{item.label}</span>
                      <span className="breakdown-value">{item.value}</span>
                    </div>
                  ))}
                </div>
              )}
              {!stat.stats && !stat.change && (
                <span className="stat-subtitle">{stat.subtitle}</span>
              )}
              {stat.detail && (
                <div className="info-content">
                <div className="info-details">
                  {stat.detail.map((item, idx) => (
                    <span className="info-badge" key={idx}>{item.label}: {item.value}</span>
                  ))}
                </div>
              </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* 하단 차트 섹션 */}
      <div className="charts-section">
        <div className="chart-card">
          <h3>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-message-square w-5 h-5 text-gray-600" aria-hidden="true"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
            <span>알림톡 발송 상세</span>
            </h3>
          <div className="broadcast-details">
            <div className="broadcast-item">
                <div className="broadcast-label-box">
                    <div className='broadcast-label'>발송 성공</div>
                    <div className="broadcast-sub-label">정상 발송 완료</div>
                </div>
                <div className="broadcast-value-box">
                    <div className="broadcast-value positive">{dashboardData?.alimtalk?.success || '0'}건</div>
                    <div className="broadcast-change">{(dashboardData?.alimtalk?.success / dashboardData?.alimtalk?.total * 100).toFixed(2) || '0.0'}%</div>
                </div>
            </div>
            <div className="broadcast-item">
                <div className="broadcast-label-box">
                    <div className='broadcast-label'>발송 실패</div>
                    <div className="broadcast-sub-label">번호 오류, 차단 등</div>
                </div>
                <div className="broadcast-value-box">
                    <div className="broadcast-value negative">{dashboardData?.alimtalk?.fail || '0'}건</div>
                    <div className="broadcast-change">{(dashboardData?.alimtalk?.fail / dashboardData?.alimtalk?.total * 100).toFixed(2) || '0.0'}%</div>
                </div>
            </div>
            <div className="broadcast-item total">
              <span className="broadcast-label">총 발송량</span>
              <span className="broadcast-value">{dashboardData?.alimtalk?.total || '0'}건</span>
            </div>
          </div>
        </div>
      </div>

      {/* 추가 정보 카드들 */}
      <div className="info-cards">
        <div className="info-card">
          <div className="info-header">
            <span>이번 달 고객 생성 횟수</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-file-text text-gray-400" aria-hidden="true"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M10 9H8"></path><path d="M16 13H8"></path><path d="M16 17H8"></path></svg>
          </div>
          <div className="info-content">
            <span className="info-number">{dashboardData?.createCustomer?.thisMonth || '0'}건</span>
            <div className="info-details">
              <span className="info-badge">플레이스: {dashboardData?.createCustomer?.detail?.thisMonth?.place || '0'}건</span>
              <span className="info-badge">인스타: {dashboardData?.createCustomer?.detail?.thisMonth?.insta || '0'}건</span>
              <span className="info-badge">블로그: {dashboardData?.createCustomer?.detail?.thisMonth?.blog || '0'}건</span>
              <span className="info-badge">기타: {dashboardData?.createCustomer?.detail?.thisMonth?.etc || '0'}건</span>
            </div>
          </div>
        </div>

        <div className="info-card">
          <div className="info-header">
            <span className="red-text">관리가 소홀한 업체</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-clock text-gray-400" aria-hidden="true"><path d="M12 6v6l4 2"></path><circle cx="12" cy="12" r="10"></circle></svg>         
          </div>
          <div className="info-content">
            <span className="info-number">{dashboardData?.negligenceCustomer || '0'}건</span>
            <span className="info-subtitle">최근 7일간 업데이트가 없는 업체</span>
          </div>
        </div>

        <div className="info-card">
          <div className="info-header">
            <span>리포트 열람 현황</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-target text-gray-400" aria-hidden="true"><circle cx="12" cy="12" r="10"></circle><circle cx="12" cy="12" r="6"></circle><circle cx="12" cy="12" r="2"></circle></svg>
          </div>
          <div className="info-content">
            <span className="info-number">{dashboardData?.viewContentCounts?.view || '0'} / {dashboardData?.viewContentCounts?.total || '0'} 건</span>
          </div>
        </div>
      </div>

      {/* 추가 분석 섹션 */}
      <div className="analysis-section onebox">
        {/* 월별 생성 추이 */}
        <div className="analysis-card">
          <div className="analysis-header">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-calendar" aria-hidden="true"><path d="M8 2v4"></path><path d="M16 2v4"></path><rect width="18" height="18" x="3" y="4" rx="2"></rect><path d="M3 10h18"></path></svg>
            <span>월별 생성 추이</span>
          </div>
          <p className="analysis-subtitle">최근 6개월간 콘텐츠 생성 현황</p>
          
          <div className="monthly-trend">
            {dashboardData?.monthlyCounts?.map((item, idx) => (
              <div className="current-month">
                <div className="current-label-box">
                  <span className="month-label">{item.year}년 {item.month}월</span>
                  <div className="month-count-box">
                    <span className="month-count">{item.count || '0'}건</span>
                  </div>
                </div>
              </div>
            ))}
            
          </div>
        </div>
      </div>

      </main>
    </div>
  );
};

export default Dashboard; 